import { useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAccounts } from "@/api/accounts";
import { useCreateTransfer } from "@/api/transfers";
import { AmountError } from "@/money";
import {
  EmptyState,
  ErrorNote,
  Field,
  Loading,
  Panel,
  PageTitle,
  buttonClass,
  inputClass,
} from "@/components/ui";
import { violationsOf } from "@/i18n/apiMessages";
import { useFormat } from "@/i18n/format";
import { useT } from "@/i18n/LocaleProvider";
import { today } from "@/dates";

/**
 * Moving money between two of the household's own accounts (`model.md` §5.7).
 *
 * It is a separate page from the transaction form on purpose. A transfer is not
 * an expense with a strange category — it has no category at all, it has two
 * accounts instead of one, and it changes two balances. Folding it into the
 * entry form as a third direction would put a field on the fast path that
 * belongs to a rarer, different operation, and K-15 measured what that costs.
 *
 * There is no category picker here, and there is nothing to hide: the two legs
 * carry one split each with no category, and the schema refuses anything else.
 */
export function NewTransferPage() {
  const t = useT();
  const format = useFormat();
  const { householdId = "" } = useParams();
  const navigate = useNavigate();

  const accounts = useAccounts(householdId);
  const create = useCreateTransfer(householdId);

  const list = accounts.data?.data ?? [];
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [amount, setAmount] = useState("");
  const [received, setReceived] = useState("");
  const [bookedDate, setBookedDate] = useState(today());
  const [note, setNote] = useState("");
  const [amountError, setAmountError] = useState<string>();

  const from = list.find((a) => a.id === (fromId || list[0]?.id));
  // The destination defaults to the first account that is not the source: the
  // one thing a transfer cannot be is money moving to where it already is.
  const to = list.find((a) => a.id === (toId || list.find((a) => a.id !== from?.id)?.id));
  const crossCurrency = Boolean(from && to && from.currency_code !== to.currency_code);

  const violation = violationsOf(t, create.error);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setAmountError(undefined);

    if (!from || !to) {
      setAmountError(t("transfer.chooseTwoAccounts"));
      return;
    }

    let minor: number;
    let arriving: number | undefined;
    try {
      minor = Math.abs(format.parse(amount, from.currency_code));
      if (crossCurrency) {
        arriving = Math.abs(format.parse(received, to.currency_code));
      }
    } catch (err) {
      setAmountError(err instanceof AmountError ? t(err.key, err.params) : String(err));
      return;
    }
    if (minor === 0) {
      setAmountError(t("transaction.amountNotZero"));
      return;
    }

    create.mutate(
      {
        from_account_id: from.id,
        to_account_id: to.id,
        booked_date: bookedDate,
        amount_minor: minor,
        ...(arriving !== undefined ? { received_amount_minor: arriving } : {}),
        ...(note ? { note } : {}),
      },
      { onSuccess: () => navigate(`/households/${householdId}/transactions`) },
    );
  }

  if (accounts.isLoading) return <Loading />;

  if (list.length < 2) {
    return (
      <div className="max-w-md">
        <PageTitle>{t("transactions.transfer")}</PageTitle>
        <EmptyState title={t("transfer.needTwoTitle")}>{t("transfer.needTwoBody")}</EmptyState>
      </div>
    );
  }

  return (
    <div className="max-w-md">
      <PageTitle>{t("transactions.transfer")}</PageTitle>

      <Panel>
      <form onSubmit={onSubmit} noValidate>
        <Field label={t("transfer.from")} htmlFor="transfer-from" error={violation("from_account_id")}>
          <select
            id="transfer-from"
            name="from_account_id"
            value={from?.id ?? ""}
            onChange={(e) => setFromId(e.target.value)}
            className={`${inputClass} min-h-11 text-base sm:text-sm`}
          >
            {list.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.currency_code})
              </option>
            ))}
          </select>
        </Field>

        <Field label={t("transfer.to")} htmlFor="transfer-to" error={violation("to_account_id")}>
          <select
            id="transfer-to"
            name="to_account_id"
            value={to?.id ?? ""}
            onChange={(e) => setToId(e.target.value)}
            className={`${inputClass} min-h-11 text-base sm:text-sm`}
          >
            {list
              .filter((a) => a.id !== from?.id)
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.currency_code})
                </option>
              ))}
          </select>
        </Field>

        <Field
          label={`${t("transactions.amount")} (${from?.currency_code ?? "EUR"})`}
          htmlFor="transfer-amount"
          hint={t("transfer.amountHint")}
          error={amountError ?? violation("amount_minor")}
        >
          <input
            id="transfer-amount"
            name="amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            placeholder={format.plain(50_000, from?.currency_code ?? "EUR")}
            autoFocus
            className={`${inputClass} num min-h-12 text-lg`}
          />
        </Field>

        {crossCurrency && (
          <Field
            label={`${t("transfer.arrived")} (${to?.currency_code})`}
            htmlFor="transfer-received"
            hint={t("transfer.arrivedHint")}
            error={violation("received_amount_minor")}
          >
            <input
              id="transfer-received"
              name="received_amount_minor"
              value={received}
              onChange={(e) => setReceived(e.target.value)}
              inputMode="decimal"
              placeholder={format.plain(47_000, to?.currency_code ?? "EUR")}
              className={`${inputClass} num min-h-12 text-lg`}
            />
          </Field>
        )}

        <Field label={t("transactions.date")} htmlFor="transfer-date" error={violation("booked_date")}>
          <input
            id="transfer-date"
            name="booked_date"
            type="date"
            value={bookedDate}
            onChange={(e) => setBookedDate(e.target.value)}
            className={`${inputClass} min-h-11 text-base sm:text-sm`}
          />
        </Field>

        <Field label={`${t("transaction.note")} (${t("common.optional")})`} htmlFor="transfer-note">
          <input
            id="transfer-note"
            name="note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={`${inputClass} min-h-11 text-base sm:text-sm`}
          />
        </Field>

        <button
          type="submit"
          disabled={create.isPending}
          className={`${buttonClass} mt-6 min-h-12 w-full text-base sm:text-sm`}
        >
          {create.isPending ? t("common.saving") : t("transfer.save")}
        </button>
      </form>
      </Panel>

      <ErrorNote error={create.error} />
    </div>
  );
}
